//
// # Selection Descriptor Helpers
//
// Pure functions that build a `EntityDescriptor` view model from a live
// EditorCore entity. They read entity fields defensively (no type assertions)
// so the workspace stays decoupled from EditorCore-internal entity types.
//
// These cover the synchronous fields only (name, position, rotation, pin
// direction/layer/pinType). Async generator params (`params` / `paramSpec`)
// are fetched separately by the panel via `get-instance`; here we report
// `paramsLoading: true` for instance kinds so the panel knows to fetch.
//

import { Ident, model } from "GenAlphaModel";
import type { schema } from "GenAlphaSchema";
import { readPinType, readPortDir } from "./schemaEnums";
import type {
  DescriptorBbox,
  DescriptorPoint,
  EntityDescriptor,
  WorkspaceSelection,
} from "./types";

// Read a property off an unknown value without type assertions or `x is T`
// type-guard predicates (both lint-forbidden). Returns `undefined` when the
// value is not an indexable object. `Reflect.get` accepts an `object`, so we
// only need the (non-narrowing) object check.
function getField(value: unknown, key: string): unknown {
  if (typeof value !== "object" || value === null) return undefined;
  return Reflect.get(value, key);
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

// Narrow a number to the schema Rotation union (0/90/180/270), default 0.
export function toRotation(value: number): schema.Rotation {
  switch (value) {
    case 90:
      return 90;
    case 180:
      return 180;
    case 270:
      return 270;
    default:
      return 0;
  }
}

// Read a `{ x, y }` point off an unknown value.
function readPoint(value: unknown): DescriptorPoint | undefined {
  const x = readNumber(getField(value, "x"));
  const y = readNumber(getField(value, "y"));
  if (x === undefined || y === undefined) return undefined;
  return { x, y };
}

// Read a `{ min: {x,y}, max: {x,y} }` bounding box off an unknown value.
function readBbox(value: unknown): DescriptorBbox | undefined {
  const min = readPoint(getField(value, "min"));
  const max = readPoint(getField(value, "max"));
  if (min === undefined || max === undefined) return undefined;
  return { min, max };
}

// Read a schema Xform-like `{ x, y, rotation, reflected }` off an unknown value.
function readXform(value: unknown):
  | {
      position: DescriptorPoint;
      rotation: number;
      reflected: boolean;
    }
  | undefined {
  const x = readNumber(getField(value, "x"));
  const y = readNumber(getField(value, "y"));
  const rotation = readNumber(getField(value, "rotation"));
  const reflected = readBoolean(getField(value, "reflected"));
  if (
    x === undefined ||
    y === undefined ||
    rotation === undefined ||
    reflected === undefined
  ) {
    return undefined;
  }
  return { position: { x, y }, rotation, reflected };
}

// Structural equality between two selections (for skipping redundant updates).
export function selectionEq(
  a: WorkspaceSelection | null,
  b: WorkspaceSelection | null
): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return (
    Ident.stringify(a.designIdent) === Ident.stringify(b.designIdent) &&
    a.info.entityKind === b.info.entityKind &&
    a.info.id === b.info.id &&
    a.info.count === b.info.count
  );
}

// -- Schematic instance --

export function describeSchInstance(
  entity: unknown,
  designIdent: Ident
): EntityDescriptor | null {
  const data = getField(entity, "data");
  const id = readNumber(getField(data, "id"));
  if (id === undefined) return null;
  const name = readString(getField(data, "name"));
  if (name === undefined) return null;
  const xform = readXform(getField(data, "xform"));
  if (xform === undefined) return null;

  // Type name is the target's display name (the symbol/generator cell).
  let typeName = "";
  const targetResult = Ident.validator.validate(getField(data, "target"));
  if (!targetResult.err) {
    typeName = Ident.displayName(targetResult.val);
  }

  return {
    kind: "instance",
    designIdent,
    id,
    typeName,
    name,
    position: xform.position,
    rotation: xform.rotation,
    reflected: xform.reflected,
    paramsLoading: true,
  };
}

// -- Schematic pin --

export function describeSchPin(
  entity: unknown,
  designIdent: Ident
): EntityDescriptor | null {
  const data = getField(entity, "data");
  const id = readNumber(getField(data, "id"));
  if (id === undefined) return null;
  const name = readString(getField(data, "portName"));
  if (name === undefined) return null;
  const direction = readPortDir(getField(data, "direction"));
  if (direction === undefined) return null;
  const xform = readXform(getField(data, "xform"));
  if (xform === undefined) return null;

  return {
    kind: "schPin",
    designIdent,
    id,
    name,
    direction,
    position: xform.position,
    rotation: xform.rotation,
    reflected: xform.reflected,
  };
}

// -- Layout instance --

export function describeLayoutInstance(
  entity: unknown,
  designIdent: Ident,
  showOutlines: boolean
): EntityDescriptor | null {
  const data = getField(entity, "data");
  const id = readNumber(getField(data, "id"));
  if (id === undefined) return null;
  const name = readString(getField(data, "name"));
  if (name === undefined) return null;

  // Position/rotation/reflected: only well-defined for a scalar placement.
  // For segmented/array/unbound placements we fall back to the origin and an
  // identity orientation (Phase 5 deepens layout-instance editing).
  let position: DescriptorPoint = { x: 0, y: 0 };
  let rotation = 0;
  let reflected = false;
  const place = getField(data, "place");
  if (getField(place, "kind") === "scalar") {
    const layoutXform = getField(place, "xform");
    const loc = readPoint(getField(layoutXform, "loc"));
    if (loc !== undefined) position = loc;
    const reflect = getField(layoutXform, "reflect");
    const explicitRotation = readNumber(getField(layoutXform, "rotation")) ?? 0;
    const horiz = readBoolean(getField(reflect, "horiz"));
    const vert = readBoolean(getField(reflect, "vert"));
    if (horiz !== undefined && vert !== undefined) {
      const orientation = model.layout.LayoutXform.convertSomeOrientation({
        loc: position,
        reflect: { horiz, vert },
        rotation: toRotation(explicitRotation),
      });
      rotation = orientation.rotation;
      reflected = orientation.reflected;
    } else {
      rotation = explicitRotation;
    }
  }

  // Read-only cell-boundary outline from the target cell's `abs.outline`
  // (in local cell coords). Only bound targets carry a resolved `layout`;
  // unbound/segmented placements have no `layout` and yield `undefined`.
  const target = getField(data, "target");
  const targetLayout = getField(target, "layout");
  const outlineExtents = readBbox(
    getField(getField(targetLayout, "abs"), "outline")
  );

  return {
    kind: "layoutInstance",
    designIdent,
    id,
    name,
    position,
    rotation,
    reflected,
    paramsLoading: true,
    outlineExtents,
    showOutlines,
  };
}

// -- Layout pin --

export function describeLayoutPin(
  entity: unknown,
  designIdent: Ident
): EntityDescriptor | null {
  const data = getField(entity, "data");
  const id = readNumber(getField(data, "id"));
  if (id === undefined) return null;
  const name = readString(getField(data, "name"));
  if (name === undefined) return null;
  // Layer is a resolved MetalLayer object carrying the numeric layer id.
  const layer = readNumber(getField(getField(data, "layer"), "layer")) ?? 0;
  const position = readPoint(getField(data, "loc"));
  if (position === undefined) return null;
  const pinType = readPinType(getField(data, "pinType"));

  return {
    kind: "layoutPin",
    designIdent,
    id,
    name,
    layer,
    pinType,
    position,
  };
}
